import { useMemo, useState } from "react"
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Hash, Loader2 } from "lucide-react"
import { ChannelThread } from "@/components/channels/ChannelThread"
import { ChannelSettingsDialog } from "@/components/channels/ChannelSettingsDialog"
import { useActiveEvent } from "@/core/active-event"
import { channelsQueryOptions } from "@/core/channels"

export const Route = createFileRoute("/$orgSlug/$eventSlug/c/$channelId")({
  component: ChannelThreadView,
})

// Full-screen channel thread — the narrow-canvas counterpart to the channels
// reading-pane split. Resolves the channel from the channels list (same cache
// key as the list view, so realtime patches flow through here too).
function ChannelThreadView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/c/$channelId" })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { eventId } = useActiveEvent()
  const [settingsOpen, setSettingsOpen] = useState(false)

  const channelsQ = useQuery({ ...channelsQueryOptions(eventId ?? ""), enabled: !!eventId })
  const channel = useMemo(
    () => (channelsQ.data ?? []).find((c) => c.id === params.channelId) ?? null,
    [channelsQ.data, params.channelId],
  )

  const backToList = () =>
    navigate({
      to: "/$orgSlug/$eventSlug/channels",
      params: { orgSlug: params.orgSlug, eventSlug: params.eventSlug },
    })

  if (channelsQ.isLoading) {
    // Mirror the loaded ChannelThread → MessageThread frame: a fixed 52px header
    // bar (square avatar + two-line title), a flex-1 message body, and a composer
    // footer, so the layout doesn't jump when the channel resolves.
    return (
      <div className="flex h-full flex-col bg-surface">
        <header className="flex h-[52px] shrink-0 items-center gap-2.5 border-b border-plum-200/50 pr-1.5 pl-3 dark:border-white/10">
          <div className="h-7 w-7 shrink-0 animate-pulse rounded-lg bg-plum-100 dark:bg-white/10" />
          <div className="min-w-0 space-y-1.5">
            <div className="h-3.5 w-32 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
            <div className="h-2.5 w-44 animate-pulse rounded bg-plum-100 dark:bg-white/10" />
          </div>
        </header>
        <div className="canvas-bg flex flex-1 items-center justify-center text-plum-700/40 dark:text-plum-200/30">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
        <div className="shrink-0 border-t border-plum-200/50 p-3 dark:border-white/10">
          <div className="h-10 w-full animate-pulse rounded-xl bg-plum-100 dark:bg-white/10" />
        </div>
      </div>
    )
  }

  if (!channel) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="max-w-sm text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
              <Hash className="h-5 w-5" />
            </div>
            <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
              Channel not found
            </h2>
            <p className="mt-1.5 text-[13px] leading-relaxed text-plum-700/55 dark:text-plum-200/45">
              It may have been renamed or archived.
            </p>
            <button
              onClick={backToList}
              className="mt-4 rounded-lg px-3.5 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:brightness-110"
              style={{ background: "var(--accent)" }}
            >
              Back to channels
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <ChannelThread
        key={channel.id}
        channel={channel}
        onBack={backToList}
        closeVariant="back"
        onOpenSettings={() => setSettingsOpen(true)}
      />
      <ChannelSettingsDialog
        open={settingsOpen}
        channel={channel}
        onOpenChange={setSettingsOpen}
        onRenamed={() => {
          void qc.invalidateQueries({ queryKey: ["resource", "conversations"] })
        }}
        onArchivedToggled={(nowArchived) => {
          setSettingsOpen(false)
          void qc.invalidateQueries({ queryKey: ["resource", "conversations"] })
          if (nowArchived) backToList()
        }}
      />
    </>
  )
}
